/**
 * MUI theme for MafertSystem — built from designTokens.js.
 * Dark brand theme aligned to Figma Gate 2 AppShell.
 */
import { createTheme } from '@mui/material/styles';
import {
  colorTokens,
  layoutTokens,
  radiusTokens,
  shadowTokens,
  spacingTokens,
  typographyTokens,
} from './designTokens';

const { semantic } = colorTokens;

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colorTokens.brand.primary,
      light: colorTokens.brand.primaryLight,
      dark: colorTokens.brand.primaryDark,
      contrastText: colorTokens.neutral.white,
    },
    secondary: {
      main: colorTokens.brand.secondary,
      light: colorTokens.brand.secondaryLight,
      dark: colorTokens.brand.secondaryDark,
      contrastText: semantic.text.inverse,
    },
    success: {
      main: semantic.success,
    },
    warning: {
      main: semantic.warning,
    },
    error: {
      main: semantic.error,
    },
    info: {
      main: semantic.info,
    },
    background: {
      default: semantic.bg.canvas,
      paper: semantic.bg.surface,
    },
    text: {
      primary: semantic.text.primary,
      secondary: semantic.text.secondary,
      disabled: semantic.text.disabled,
    },
    divider: semantic.border.subtle,
    action: {
      hover: 'rgba(255, 255, 255, 0.04)',
      selected: semantic.bg.surfaceSelected,
      disabled: semantic.text.disabled,
      disabledBackground: 'rgba(255, 255, 255, 0.08)',
    },
  },
  typography: {
    fontFamily: typographyTokens.fontFamily,
    h4: {
      fontFamily: typographyTokens.fontFamilyDisplay,
      fontWeight: typographyTokens.fontWeightBold,
    },
    h5: {
      fontFamily: typographyTokens.fontFamilyDisplay,
      fontWeight: typographyTokens.fontWeightBold,
    },
    h6: {
      fontFamily: typographyTokens.fontFamily,
      fontWeight: typographyTokens.fontWeightSemibold,
    },
    subtitle1: {
      fontWeight: typographyTokens.fontWeightSemibold,
    },
    button: {
      fontWeight: typographyTokens.fontWeightBold,
      textTransform: 'none',
    },
  },
  spacing: spacingTokens.unit,
  shape: {
    borderRadius: radiusTokens.md,
  },
  shadows: [
    'none',
    shadowTokens.sm,
    shadowTokens.md,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
    shadowTokens.lg,
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: semantic.bg.canvas,
          color: semantic.text.primary,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'transparent',
      },
      styleOverrides: {
        root: {
          backgroundColor: semantic.bg.topbar,
          backgroundImage: 'none',
          boxShadow: shadowTokens.appBar,
          borderBottom: `1px solid ${semantic.border.subtle}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: semantic.bg.sidebar,
          backgroundImage: 'none',
          borderRight: `1px solid ${semantic.border.subtle}`,
          width: layoutTokens.sidebarWidth,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radiusTokens.card,
          boxShadow: shadowTokens.card,
          backgroundImage: 'none',
          border: `1px solid ${semantic.border.subtle}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: radiusTokens.card,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radiusTokens.dialog,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radiusTokens.chip,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: `${layoutTokens.topbarHeight}px !important`,
          height: layoutTokens.topbarHeight,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: `${radiusTokens.navItem}px`,
          border: '1px solid transparent',
          boxSizing: 'border-box',
          '&.Mui-selected': {
            backgroundColor: semantic.bg.surfaceSelected,
            borderColor: semantic.status.brand.border,
            color: semantic.text.primary,
            '&:hover': {
              backgroundColor: semantic.bg.surfaceSelected,
            },
            '& .MuiListItemIcon-root': {
              color: colorTokens.brand.primary,
            },
          },
          '&:hover': {
            backgroundColor: semantic.action.secondary.bg,
          },
          '&:focus-visible': {
            outline: `2px solid ${semantic.status.brand.border}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: semantic.text.secondary,
        },
      },
    },
  },
});

export { layoutTokens } from './designTokens';
